import React, { useState, Children, Component } from 'react';
import { XIcon, Building, Building2, Home, Store, Warehouse, MapPin, Map, Globe, Users, User, UserCircle, Briefcase, Package, Archive, Folder, FolderOpen, FileText, Target, Flag, Zap, Settings, Hammer, Construction, CheckIcon, BoxIcon } from 'lucide-react';
interface OrgUnitTypeFormProps {
  orgUnitType?: any;
  onClose: () => void;
  onSave: (data: any) => void;
  locale: string;
}
// Available icons for org unit types
const AVAILABLE_ICONS = [{
  name: 'Building',
  icon: Building,
  category: 'Structures'
}, {
  name: 'Building2',
  icon: Building2,
  category: 'Structures'
}, {
  name: 'Home',
  icon: Home,
  category: 'Structures'
}, {
  name: 'Store',
  icon: Store,
  category: 'Structures'
}, {
  name: 'Warehouse',
  icon: Warehouse,
  category: 'Structures'
}, {
  name: 'MapPin',
  icon: MapPin,
  category: 'Locations'
}, {
  name: 'Map',
  icon: Map,
  category: 'Locations'
}, {
  name: 'Globe',
  icon: Globe,
  category: 'Locations'
}, {
  name: 'Users',
  icon: Users,
  category: 'People'
}, {
  name: 'User',
  icon: User,
  category: 'People'
}, {
  name: 'UserCircle',
  icon: UserCircle,
  category: 'People'
}, {
  name: 'Briefcase',
  icon: Briefcase,
  category: 'Operations'
}, {
  name: 'Package',
  icon: Package,
  category: 'Operations'
}, {
  name: 'BoxIcon',
  icon: BoxIcon,
  category: 'Operations'
}, {
  name: 'Archive',
  icon: Archive,
  category: 'Operations'
}, {
  name: 'Folder',
  icon: Folder,
  category: 'Organization'
}, {
  name: 'FolderOpen',
  icon: FolderOpen,
  category: 'Organization'
}, {
  name: 'FileText',
  icon: FileText,
  category: 'Organization'
}, {
  name: 'Target',
  icon: Target,
  category: 'Projects'
}, {
  name: 'Flag',
  icon: Flag,
  category: 'Projects'
}, {
  name: 'Zap',
  icon: Zap,
  category: 'Projects'
}, {
  name: 'Hammer',
  icon: Hammer,
  category: 'Construction'
}, {
  name: 'Construction',
  icon: Construction,
  category: 'Construction'
}, {
  name: 'Settings',
  icon: Settings,
  category: 'Technical'
}, {
  name: 'BoxIcon',
  icon: BoxIcon,
  category: 'Technical'
}];
export function OrgUnitTypeForm({
  orgUnitType,
  onClose,
  onSave,
  locale
}: OrgUnitTypeFormProps) {
  const [formData, setFormData] = useState({
    code: orgUnitType?.code || '',
    name: orgUnitType?.name || '',
    description: orgUnitType?.description || '',
    icon: orgUnitType?.icon || 'Building',
    isActive: orgUnitType?.isActive ?? true,
    allowsChildren: orgUnitType?.allowsChildren ?? true,
    canBeRoot: orgUnitType?.canBeRoot ?? false
  });
  const [showIconPicker, setShowIconPicker] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validation
    if (!formData.code.trim()) {
      alert('Code is required');
      return;
    }
    if (!formData.name.trim()) {
      alert('Name is required');
      return;
    }
    onSave(formData);
  };
  const selectedIconData = AVAILABLE_ICONS.find(i => i.name === formData.icon);
  const SelectedIcon = selectedIconData?.icon || Building;
  return <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50" data-id="element-20272">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-20273">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-20274">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-20275">
            {orgUnitType ? 'Edit Organisation Unit Type' : 'Add Organisation Unit Type'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500" data-id="element-20276">
            <XIcon className="h-6 w-6" data-id="element-20277" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6" data-id="element-20278">
          {/* Icon Selection */}
          <div data-id="element-20279">
            <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-20280">Icon</h4>
            <div className="space-y-3" data-id="element-20281">
              <div className="flex items-center space-x-3" data-id="element-20282">
                <div className="flex-shrink-0 rounded-lg p-3 bg-indigo-100 text-indigo-600" data-id="element-20283">
                  <SelectedIcon className="h-8 w-8" data-id="element-20284" />
                </div>
                <div className="flex-1" data-id="element-20285">
                  <button type="button" onClick={() => setShowIconPicker(!showIconPicker)} className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20286">
                    {showIconPicker ? 'Close Icon Picker' : 'Change Icon'}
                  </button>
                </div>
              </div>

              {showIconPicker && <div className="border border-gray-200 rounded-lg p-4 bg-gray-50" data-id="element-20287">
                  <div className="grid grid-cols-6 gap-2" data-id="element-20288">
                    {AVAILABLE_ICONS.map(iconData => {
                  const IconComponent = iconData.icon;
                  const isSelected = formData.icon === iconData.name;
                  return <button key={iconData.name} type="button" onClick={() => {
                    setFormData({
                      ...formData,
                      icon: iconData.name
                    });
                    setShowIconPicker(false);
                  }} className={`relative p-3 rounded-lg border-2 transition-all ${isSelected ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50'}`} title={iconData.name} data-id="element-20289">
                          <IconComponent className={`h-6 w-6 mx-auto ${isSelected ? 'text-indigo-600' : 'text-gray-600'}`} data-id="element-20290" />
                          {isSelected && <div className="absolute -top-1 -right-1 bg-indigo-600 rounded-full p-0.5" data-id="element-20291">
                              <CheckIcon className="h-3 w-3 text-white" data-id="element-20292" />
                            </div>}
                        </button>;
                })}
                  </div>
                  <p className="mt-3 text-xs text-gray-500 text-center" data-id="element-20293">
                    Click an icon to select it
                  </p>
                </div>}
            </div>
          </div>

          {/* Basic Information */}
          <div data-id="element-20294">
            <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-20295">
              Basic Information
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-20296">
              <div data-id="element-20297">
                <label className="block text-sm font-medium text-gray-700" data-id="element-20298">
                  Code *
                </label>
                <input type="text" required value={formData.code} onChange={e => setFormData({
                ...formData,
                code: e.target.value.toUpperCase()
              })} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm uppercase" placeholder="e.g., ENTITY" maxLength={20} data-id="element-20299" />
                <p className="mt-1 text-xs text-gray-500" data-id="element-20300">
                  Unique identifier (uppercase, no spaces)
                </p>
              </div>

              <div data-id="element-20301">
                <label className="block text-sm font-medium text-gray-700" data-id="element-20302">
                  Name *
                </label>
                <input type="text" required value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="e.g., Legal Entity" data-id="element-20303" />
              </div>

              <div className="sm:col-span-2" data-id="element-20304">
                <label className="block text-sm font-medium text-gray-700" data-id="element-20305">
                  Description
                </label>
                <textarea value={formData.description} onChange={e => setFormData({
                ...formData,
                description: e.target.value
              })} rows={3} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="Optional description of this org unit type" data-id="element-20306" />
              </div>
            </div>
          </div>

          {/* Hierarchy Settings */}
          <div data-id="element-20307">
            <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-20308">
              Hierarchy Settings
            </h4>
            <div className="space-y-4" data-id="element-20309">
              <div className="flex items-start" data-id="element-20310">
                <div className="flex items-center h-5" data-id="element-20311">
                  <input type="checkbox" checked={formData.allowsChildren} onChange={e => setFormData({
                  ...formData,
                  allowsChildren: e.target.checked
                })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-20312" />
                </div>
                <div className="ml-3" data-id="element-20313">
                  <label className="text-sm font-medium text-gray-900" data-id="element-20314">
                    Allows Children
                  </label>
                  <p className="text-xs text-gray-500" data-id="element-20315">
                    This type can have child organisation units beneath it
                  </p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-20316">
                <div className="flex items-center h-5" data-id="element-20317">
                  <input type="checkbox" checked={formData.canBeRoot} onChange={e => setFormData({
                  ...formData,
                  canBeRoot: e.target.checked
                })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-20318" />
                </div>
                <div className="ml-3" data-id="element-20319">
                  <label className="text-sm font-medium text-gray-900" data-id="element-20320">
                    Can Be Root Level
                  </label>
                  <p className="text-xs text-gray-500" data-id="element-20321">
                    This type can exist at the root level without a parent
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Status */}
          <div data-id="element-20322">
            <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-20323">Status</h4>
            <div className="flex items-start" data-id="element-20324">
              <div className="flex items-center h-5" data-id="element-20325">
                <input type="checkbox" checked={formData.isActive} onChange={e => setFormData({
                ...formData,
                isActive: e.target.checked
              })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-20326" />
              </div>
              <div className="ml-3" data-id="element-20327">
                <label className="text-sm font-medium text-gray-900" data-id="element-20328">
                  Active
                </label>
                <p className="text-xs text-gray-500" data-id="element-20329">
                  Inactive types cannot be used for new organisation units
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200" data-id="element-20330">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20331">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20332">
              {orgUnitType ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>;
}